<template>
    <div class="tw-quantity-select__btn-group" v-if="!disableButton">
        <button type="button" class="tw-btn tw-btn--ghost tw-btn--lg" v-on:click="subQuantity()" :disabled="currentQuantity === 0">
            <font-awesome-icon :icon="['fas', 'minus']" />
        </button>
        <div class="tw-quantity-select__select" v-bind:class="{ 'tw-quantity-select__select--disabled': disabled }">
            <select v-model="currentQuantity" :disabled="disabled" v-show="!disabled">
                <option v-for="quantity in getQuantityOptions()" :value="quantity" :key="quantity">{{quantity}}</option>
            </select>
            <div class="tw-quantity-select__selectcover tw-text--center">{{ currentQuantity || 0 }}</div>
        </div>
        <button type="button" class="tw-btn tw-btn--ghost tw-btn--lg" :disabled="isClickable()" v-on:click="addQuantity()">
            <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
    </div>
</template>

<script lang='ts'>
    import { Vue } from "vue-property-decorator";
    import { Component, Prop, Emit, Watch } from "vue-property-decorator";
    import TicketWidgetComponent from '../../TicketWidgetComponent';
    import CartManager from '../../../services/CartManager';
    import Event from '../../../models/Event';
    import TicketType from "../../../models/TicketType";

    // font awesome
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';

    library.add(faPlus, faMinus);

    @Component({
        components: {
            FontAwesomeIcon
        }
    })

    export default class QuantitySelect extends TicketWidgetComponent {
        @Prop() public available!: number;
        @Prop() public ticketId!: string;
        @Prop() public disableButton!: boolean;

        public disabled: boolean = false;
        public currentQuantity: number = 0;
        public quantityOptions: number[] = [];
        public eventTicketType: TicketType;

        constructor() {
            super();

            // get the event version of the ticket type
            this.eventTicketType = this.selectedEvent.getTicketType(this.ticketId);
        }

        mounted() {
            this.setUp();
        }

        setUp() {
            this.quantityOptions = this.getQuantityOptions();

            this.disabled = this.available === 0 ? true : false;

            if(this.eventTicketType.bundle_ticket_type.length) {
                this.disabled = true;
            }

            // add listener for updating the cart
            this.$root.$on('updateCart', () => {
                this.checkBundledTickets();
            });
        }

        /**
        * @desc Gets the list of numbers that will be available to select from
        * @return {Array<number>}
        */
        getQuantityOptions() : number[] {
            let item = this.selectedEvent.getTicketType(this.ticketId);
            let quantities = [0];

            let availableQuantity = this.available ? this.available : item.max_tickets_per_purchase || 99;

            // check for fixed quantities
            let fixedQuantities = item.fixed_ticket_quantities_per_purchase || [];

            if(fixedQuantities.length > 0) {
                quantities = fixedQuantities.filter((quantityOption: number) => {
                    return quantityOption <= this.available;
                });
            }
            else {
                let maxQuantity = item.max_tickets_per_purchase || 99;
                let minQuantity = item.min_tickets_per_purchase || 1;

                for(let i = minQuantity; i <= maxQuantity && i <= availableQuantity; i++) {
                    quantities.push(i);
                }
            }

            // add zero to beginning
            return quantities;
        }

        /**
        * @desc When the add button is pressed
        */
        addQuantity() {
            let quantity = this.currentQuantity || 0;
            let quantityOptions = this.getQuantityOptions();

            // find index of current option
            let quantityIndex = quantityOptions.indexOf(quantity);
            quantity = quantityIndex < quantityOptions.length ? quantityOptions[quantityIndex + 1] : 0;

            this.currentQuantity = quantity;

            this.updateCart();
        };

        /**
        * @desc When the subtract button is pressed
        */
        subQuantity() {
            let quantity = this.currentQuantity || 0;
            let quantityOptions = this.getQuantityOptions();

            // find index of current option
            let quantityIndex = quantityOptions.indexOf(quantity);
            quantity = quantityIndex > 0 ? quantityOptions[quantityIndex - 1] : 0;

            this.currentQuantity = quantity;

            this.updateCart();
        };

        /**
         * @desc checks if this ticket is a bundled_ticket
        */
        checkBundledTickets() {
            if(this.eventTicketType.bundle_ticket_type.length && this.checkBundleQuantities() > 0) {
                this.disabled = false;
            }
            else if (this.eventTicketType.bundle_ticket_type.length && this.checkBundleQuantities() === 0) {
                this.cartManager.selectedQuantities[this.ticketId] = 0;
                this.currentQuantity = 0;
                this.disabled = true;
            }
        }

        checkBundleQuantities() {
            let counter: number = 0;

            this.eventTicketType.bundle_ticket_type.forEach((bundle_ticket) => {
                counter += this.cartManager.selectedQuantities[bundle_ticket] ? this.cartManager.selectedQuantities[bundle_ticket] : 0;
            });

            return counter;
        }

        /**
         * @desc determines if the user can add more tickets
         * @return {boolean}
        */
        isClickable() {
            if(this.disabled) {
                return this.disabled;
            }

            if(!this.quantityOptions.length) {
                return true;
            }

            let highestQuantityAvailable = this.getQuantityOptions().splice(-1).pop();

            if(this.currentQuantity === highestQuantityAvailable) {
                return true;
            }

            return false;
        }

        /**
         * @desc update the cart and emit an event so other components can update too
        */
        updateCart() {
            this.cartManager.selectedQuantities[this.ticketId] = this.currentQuantity;

            // if the amount available is = to currentQuantity or the amount available has changed to 0 disable
            this.disabled = this.available === this.currentQuantity || this.available === 0 ? true : false;

            this.$root.$emit('updateCart');
        }

        /**
         * @desc this watch is set up for when the user uses the drop down instead of the buttons
        */
        @Watch('currentQuantity')
        @Watch('available')
        onQuantityChanged(val: number, oldVal: number) {
            this.updateCart();
        }
    }
</script>
